import type { FactorRangeDatum, ScaleDomain } from './factorRange.types';

const STEP = 10;
// Inert fallback for an empty factor set: the panel renders its empty state in
// that case and never draws this axis, so it asserts nothing about how a factor
// is scored.
const FULL_RANGE: ScaleDomain = {
  start: 0,
  end: 100,
  ticks: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
};

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const buildTicks = (start: number, end: number): number[] => {
  const ticks: number[] = [];
  for (let tick = start; tick <= end; tick += STEP) {
    ticks.push(tick);
  }
  return ticks;
};

/**
 * Derives the shared axis from the data. Real factors cluster into a narrow
 * band, so a fixed wide axis would waste most of the width and flatten the
 * differences between rows; instead we take the tightest STEP-aligned window
 * that still contains every marker we draw.
 */
export function getDomain(factors: readonly FactorRangeDatum[]): ScaleDomain {
  const values = factors
    .flatMap((factor) => [
      factor.min,
      factor.max,
      factor.industry,
      factor.company,
    ])
    .filter(isNumber);

  if (values.length === 0) {
    return FULL_RANGE;
  }

  const start = Math.floor(Math.min(...values) / STEP) * STEP;
  const rounded = Math.ceil(Math.max(...values) / STEP) * STEP;
  // Every value landing on one multiple of ten collapses the axis to zero
  // width, which would stack every marker on top of each other at 0%.
  const end = rounded === start ? start + STEP : rounded;

  return { start, end, ticks: buildTicks(start, end) };
}

/** Positions a score on the shared axis, as a percentage of the axis width. */
export function toPercent(value: number, domain: ScaleDomain): number {
  const span = domain.end - domain.start;
  if (span <= 0) {
    return 0;
  }
  const percent = ((value - domain.start) / span) * 100;
  return Math.min(100, Math.max(0, percent));
}
